import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Cookies from 'js-cookie';
import s from './UnitCompanionServiceLogList.module.scss';
import { FormattedMessage, injectIntl } from 'react-intl';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Banner from '../Banner/Banner';
import UnitCompanionServiceLogListTable from './UnitCompanionServiceLogListTable';
import Pagination from '../Pagination/Pagination';
import { DEFAULT_PAGE_SIZE } from '../../constants';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { compareValues } from '../../utils/';
import MaintenanceContentTemplate from '../Maintenance/MaintenanceContentTemplate';
import DateTimePicker from '../WSAControls/DateTimePicker/DateTimePicker';
import Dropdown from '../WSAControls/DroprdownContainer/Dropdown';
import moment from 'moment-timezone';
import { getUnitCompanionLogServicing, getCurrentTimezone, getUserProfileUnit } from '../../selectors/index';
import { initialUnitCompanionLogServicingState } from '../../reducers/initialState';

import {
  getUnitCompanionLogServicingListThunk,
  setCurrentPage,
  cleanUpThunk
} from '../../actions/unitCompanionLogServicing';

const envName = process.env.REACT_APP_ENV_NAME_SHORT;

const UnitCompanionServiceLogList = ({ intl }) => {
  const dispatch = useDispatch();
  const unitCompanionLogServicing = useSelector(state => getUnitCompanionLogServicing(state));
  const currentTimezone = useSelector(state => getCurrentTimezone(state));
  const userProfileUnits = useSelector(state => getUserProfileUnit(state));

  const [filterUnitId, setFilterUnitId] = useState(0);
  const [filterFrom, setFilterFrom] = useState(null);
  const [filterTo, setFilterTo] = useState(null);
  const [asendingOrder, setAsendingOrder] = useState(false);

  const offset = unitCompanionLogServicing.offset || 0;
  const limit = unitCompanionLogServicing.limit || 10000;
  const disableControls = unitCompanionLogServicing.isLoading || false;

  const organisationId = Cookies.get(`selectedorganisationid-${envName}`) || 1;
  let userUnits = [];
  if (userProfileUnits && userProfileUnits.userProfileUnits) {
    userUnits = userProfileUnits.userProfileUnits.filter(unit => unit.OrganisationId == organisationId);
  }

  const unitOptions = () => {
    let unitData = [
      {
        label: <FormattedMessage id="maintenance.unitService.allUnits" defaultMessage="All Units" />,
        value: 0
      }
    ];

    userUnits.forEach(element => {
      unitData.push({
        label: element.Name || element.UnitSerialNumber,
        value: element.UnitId
      });
    });
    return unitData;
  };

  const onUnitChange = e => {
    setFilterUnitId(e.value);
  };

  useEffect(() => {
    return () => {
      dispatch(cleanUpThunk({ ...initialUnitCompanionLogServicingState }));
    };
  }, []);

  useEffect(() => {
    dispatch(
      getUnitCompanionLogServicingListThunk({
        offset,
        limit,
        filterUnitId: filterUnitId || null,
        filterFrom: filterFrom ? filterFrom / 1000 : null,
        filterTo: filterTo ? filterTo / 1000 : null
      })
    );
  }, [getUnitCompanionLogServicingListThunk, offset, limit, filterUnitId, filterFrom, filterTo]);

  let messageId = unitCompanionLogServicing.displayMessageCode || 'none';
  const messageText = intl.formatMessage({ id: messageId, defaultMessage: messageId });

  const placeHolderTextFromDate = intl.formatMessage({
    id: 'maintenance.companionServiceLog.fromDate',
    defaultMessage: 'From Date'
  });
  const placeHolderTextToDate = intl.formatMessage({
    id: 'maintenance.companionServiceLog.toDate',
    defaultMessage: 'To Date'
  });

  const onSortOrder = () => setAsendingOrder(!asendingOrder);

  const onPageChangeHandler = currentPage => {
    setCurrentPage(currentPage);
  };

  let sessions = [...(unitCompanionLogServicing.sessions || [])];
  sessions = asendingOrder ? sessions.sort(compareValues('EndedAt')) : sessions.sort(compareValues('EndedAt', 'desc'));

  return (
    <div className={s.unitCompanionServiceLog}>
      <MaintenanceContentTemplate selectedPage="companionServiceLog">
        <div className={s.contentWrapper}>
          {unitCompanionLogServicing.isLoading && <LoadingSpinner />}
          <Banner
            failureText={messageText}
            showBanner={unitCompanionLogServicing.showBanner}
            status={unitCompanionLogServicing.isOpSuccessful}
            successText={messageText}
          />
          <div className={s.subHeading}>
            <FormattedMessage id="maintenance.companionServiceLog" defaultMessage="Companion Service Log" />
          </div>
          <Row>
            <Col sm={12}>
              <span className={s.textHeading}>
                <FormattedMessage id="maintenance.unitService.filterBy" defaultMessage="Filter By" />
              </span>
            </Col>
          </Row>
          <Row>
            <Col sm={6} lg={2}>
              <Dropdown
                id="drpUnitId"
                dataArray={unitOptions()}
                controlData={{
                  placeholderText: <FormattedMessage id="maintenance.unitService.unit" defaultMessage="Unit" />,
                  customClassName: ''
                }}
                onDropdownChange={e => onUnitChange(e)}
                selectedOption={unitOptions().filter(option => option.value === filterUnitId)}
                disabled={disableControls}
              />
            </Col>
            <Col sm={6} lg={2}>
              <DateTimePicker
                className={s.datetimeInput}
                onChange={m => setFilterFrom(m.valueOf())}
                timeFormat={null}
                dateFormat="DD-MMM-YYYY"
                checkValue={filterFrom}
                value={(filterFrom && moment(filterFrom).tz(currentTimezone)) || null}
                defaultValue={(filterFrom && moment(filterFrom).tz(currentTimezone)) || null}
                placeHolderText={placeHolderTextFromDate}
                closeOnSelect
                showClear={true}
                disabled={disableControls}
                onClear={() => setFilterFrom(null)}
              />
            </Col>
            <Col sm={6} lg={2}>
              <DateTimePicker
                className={s.datetimeInput}
                onChange={m => setFilterTo(m.valueOf())}
                timeFormat={null}
                dateFormat="DD-MMM-YYYY"
                checkValue={filterTo}
                value={(filterTo && moment(filterTo).tz(currentTimezone)) || null}
                defaultValue={(filterTo && moment(filterTo).tz(currentTimezone)) || null}
                placeHolderText={placeHolderTextToDate}
                closeOnSelect
                showClear={true}
                disabled={disableControls}
                onClear={() => setFilterTo(null)}
              />
            </Col>
          </Row>
          <div>
            <Pagination
              data={sessions}
              pageSize={DEFAULT_PAGE_SIZE}
              onPageChange={onPageChangeHandler}
              startingPage={unitCompanionLogServicing.currentPage}
            >
              <UnitCompanionServiceLogListTable currentTimezone={currentTimezone} onSortOrder={onSortOrder} asendingOrder={asendingOrder} />
            </Pagination>
          </div>
        </div>
      </MaintenanceContentTemplate>
    </div>
  );
};

export default injectIntl(UnitCompanionServiceLogList);
